import React, { useState, FC, MouseEvent } from "react";

import { QueryObserverResult } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  useApisMembersInsightPostsInsightPostCommentsCreate,
  useApisMembersInsightPostsAiCommentRequestsCreate,
  useEditorState,
  useEditorStateLinkContentGetter,
  useProvidersCurrentEmployee,
} from "~/hooks";

import { Button, DotLoadingAnimation } from "~/components/atoms";
import {
  InsightPostCommentDetailWithEditForm,
  InsightPostDetail,
  TextEditor,
} from "~/components/organisms";

import {
  InsightPostType,
  InsightPostCommentType,
  MentionSuggestEmployeeType,
  PreviewWithFileType,
  ApiMemberInsightPostsInsightPostCommentsIndexType,
  ApiMembersInsightPostsAiCommentRequestsMutateResponseType,
} from "~/domains";

type PropsType = {
  highlightInsightPostCommentId?: string;
  insightPost: InsightPostType;
  insightPostComments: InsightPostCommentType[];
  mentions: MentionSuggestEmployeeType[];
  className?: string;
  refetch: () => Promise<
    QueryObserverResult<
      ApiMemberInsightPostsInsightPostCommentsIndexType,
      unknown
    >
  >;
};

export const InsightPostCommentsWithInsightPost: FC<PropsType> = ({
  highlightInsightPostCommentId = "",
  insightPost,
  insightPostComments,
  mentions,
  className = "",
  refetch,
}: PropsType) => {
  const { currentEmployee } = useProvidersCurrentEmployee();

  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const [
    {
      value: editorState,
      onChange: setEditorState,
      jsonContent,
      hasText,
      setPlainText,
    },
  ] = useEditorState();
  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState,
    isWatch: true,
  });

  const { mutate, isLoading } =
    useApisMembersInsightPostsInsightPostCommentsCreate();

  const {
    mutate: mutateAiCommentRequest,
    isLoading: isLoadingAiCommentRequest,
  } = useApisMembersInsightPostsAiCommentRequestsCreate();
  const isRequestingAiComment =
    !!insightPost.inProgressAiCommentGenerationRequest ||
    isLoadingAiCommentRequest;

  const handleAiCommentRequest = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    mutateAiCommentRequest(
      { insightPostId: insightPost.id },
      {
        onSuccess: (
          data: ApiMembersInsightPostsAiCommentRequestsMutateResponseType,
        ) => {
          toast.success(data.message);
        },
      },
    );
  };

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!hasText) {
      toast.error("テキストが空です");
      return;
    }
    const body = {
      content: jsonContent,
      attachFiles: files,
      quotePostId: fetchedData?.id,
    };

    mutate(
      {
        insightPostId: insightPost.id,
        body,
      },
      {
        onSuccess: async () => {
          setPlainText("");
          setFiles([]);
          await refetch();
        },
      },
    );
  };

  return (
    <div className={className}>
      <div className="space-y-6">
        <InsightPostDetail
          insightPost={insightPost}
          menuItems={[]}
          openComment={() => refetch()}
        />
        <div>
          {insightPostComments.map((comment) => (
            <InsightPostCommentDetailWithEditForm
              key={comment.id}
              insightPostComment={comment}
              mentions={mentions}
              refetch={refetch}
              isHighlight={comment.id === highlightInsightPostCommentId}
            />
          ))}
        </div>
        {currentEmployee?.id === insightPost.employee.id &&
          (isRequestingAiComment ? (
            <DotLoadingAnimation className="justify-center" />
          ) : (
            <Button
              text={"かがみんに聞く"}
              color={"primary"}
              outline
              onClick={handleAiCommentRequest}
              readonly={isRequestingAiComment}
              className="w-full"
            />
          ))}
        <TextEditor
          submitButton={{
            isSubmitting: isLoading,
            onSubmit: handleSubmit,
          }}
          mentions={mentions}
          editorState={editorState}
          setEditorState={setEditorState}
          files={files}
          onChangeFiles={setFiles}
          blockQuote={fetchedData}
          isEmojiPickerTop
        />
      </div>
    </div>
  );
};
